import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { formatAudioTime } from '../../utils/messageHelpers';

/**
 * Reproductor de audio personalizado estilo Nota de Voz de WhatsApp
 */
const CustomAudioPlayer = ({ src, isAgent }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blobSrc, setBlobSrc] = useState(src);
  const audioRef = useRef(null);

  //Como las notas de voz pesan solo unos cuantos KB (formato OGG/Opus), el
  //reproductor ahora descarga el archivo en segundo plano
  //(< 100 ms) y crea un Blob local en memoria

  useEffect(() => {
    let active = true;
    let url = null;

    if (src) {
      fetch(src)
        .then(res => res.blob())
        .then(blob => {
          if (!active) return;
          url = URL.createObjectURL(blob);
          setBlobSrc(url);
        })
        .catch(() => {
          if (active) setBlobSrc(src);
        });
    }

    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (audioRef.current.duration && isFinite(audioRef.current.duration) && duration === 0) {
        setDuration(audioRef.current.duration);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const dur = audioRef.current.duration;
      if (dur && isFinite(dur) && !isNaN(dur)) {
        setDuration(dur);
      } else {
        // Truco para archivos OGG/Opus de WhatsApp que no tienen cabecera de duración fija
        audioRef.current.currentTime = 1e101;
        audioRef.current.ontimeupdate = function () {
          this.ontimeupdate = () => handleTimeUpdate();
          if (this.duration && isFinite(this.duration)) {
            setDuration(this.duration);
          }
          this.currentTime = 0;
        };
      }
    }
  };

  const handleSeek = e => {
    if (!duration || !audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const targetSec = pos * duration;
    audioRef.current.currentTime = targetSec;
    setCurrentTime(targetSec);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="d-flex align-items-center gap-2 p-2 rounded-3"
      style={{
        backgroundColor: isAgent ? 'rgba(255, 255, 255, 0.18)' : '#f8fafc',
        border: isAgent ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid #e2e8f0',
        minWidth: '220px',
        maxWidth: '280px'
      }}
    >
      <audio
        ref={audioRef}
        src={blobSrc}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />

      {/* Botón Play / Pause */}
      <button
        type="button"
        onClick={togglePlay}
        className="btn rounded-circle d-flex align-items-center justify-content-center shadow-none p-0 flex-shrink-0"
        style={{
          width: '36px',
          height: '36px',
          backgroundColor: isAgent ? '#ffffff' : '#059669',
          color: isAgent ? '#059669' : '#ffffff',
          border: 'none',
          cursor: 'pointer',
          transition: 'transform 0.15s ease'
        }}
      >
        <FontAwesomeIcon
          icon={isPlaying ? faPause : faPlay}
          style={{ fontSize: '0.85rem', marginLeft: isPlaying ? '0' : '2px' }}
        />
      </button>

      {/* Barra de progreso y tiempo */}
      <div className="flex-grow-1 d-flex flex-column gap-1">
        <div
          className="position-relative d-flex align-items-center"
          style={{ height: '14px', cursor: 'pointer' }}
          onClick={handleSeek}
        >
          <div
            className="w-100 rounded-pill overflow-hidden"
            style={{
              height: '5px',
              backgroundColor: isAgent ? 'rgba(255, 255, 255, 0.35)' : '#cbd5e1'
            }}
          >
            <div
              className="h-100 rounded-pill"
              style={{
                width: `${progress}%`,
                backgroundColor: isAgent ? '#ffffff' : '#059669',
                transition: 'width 0.1s linear'
              }}
            />
          </div>
        </div>

        {/* Timers */}
        <div
          className="d-flex justify-content-between align-items-center fw-medium"
          style={{
            fontSize: '0.7rem',
            color: isAgent ? '#ffffff' : '#64748b',
            opacity: 0.9
          }}
        >
          <span>{formatAudioTime(currentTime)}</span>
          <div className="d-flex align-items-center gap-1">
            <FontAwesomeIcon icon={faMicrophone} style={{ fontSize: '0.65rem', opacity: 0.7 }} />
            {duration > 0 && isFinite(duration) ? (
              <span>{formatAudioTime(duration)}</span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomAudioPlayer;
